// pages/admin/InternationalCollegeList.jsx (ya jahan bhi tumhari admin routing hai)
import React, { useState, useEffect } from "react";
import axios from "axios";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { MdSearch, MdPublic, MdClose } from "react-icons/md";

const InternationalCollegeList = () => {
  const base_url = import.meta.env.VITE_LOCAL_BASE_URL;

  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 50,
    totalPages: 0,
  });
  const [featuredLoading, setFeaturedLoading] = useState({});

  // View modal
  const [showModal, setShowModal] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewCollege, setViewCollege] = useState(null);
  const [viewError, setViewError] = useState("");

  const limit = 50;

  // Country list (from your data file, dedup)
  const countries = [
    "Russia", "Georgia", "Kazakhstan", "Uzbekistan",
    "Kyrgyzstan", "Philippines", "Mauritius",
  ];

  // ── Fetch list ──
  const fetchColleges = async () => {
    try {
      setLoading(true);
      const params = { page, limit };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (countryFilter) params.country = countryFilter;

      const res = await axios.get(
        `${base_url}/api/v1/international-colleges`,
        { params }
      );

      if (res.data.success) {
        // Shape: { data: { data: [...], pagination: {...} } }
        const payload = res.data.data || {};
        setData(payload.data || []);
        setPagination(
          payload.pagination || { total: 0, page: 1, limit, totalPages: 0 }
        );
      } else {
        setData([]);
      }
    } catch (error) {
      console.error("Fetch International Colleges Error:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchColleges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, countryFilter, searchTerm]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  // ── Featured toggle ──
  const toggleFeatured = async (id, currentValue) => {
    setFeaturedLoading((p) => ({ ...p, [id]: true }));
    setData((prev) =>
      prev.map((c) =>
        c.id === id ? { ...c, is_featured: !currentValue } : c
      )
    );

    try {
      await axios.patch(
        `${base_url}/api/v1/international-colleges/${id}/featured`,
        { is_featured: !currentValue }
      );
    } catch (err) {
      console.error("Toggle featured failed:", err);
      setData((prev) =>
        prev.map((c) =>
          c.id === id ? { ...c, is_featured: currentValue } : c
        )
      );
      alert(
        err?.response?.data?.message || "Failed to update featured status"
      );
    } finally {
      setFeaturedLoading((p) => ({ ...p, [id]: false }));
    }
  };

  // ── View modal ──
  const openViewModal = async (slugOrId) => {
    setShowModal(true);
    setViewLoading(true);
    setViewError("");
    setViewCollege(null);

    try {
      const res = await axios.get(
        `${base_url}/api/v1/international-colleges/${slugOrId}`
      );
      if (res.data.success) {
        setViewCollege(res.data.data);
      } else {
        setViewError(res.data.message || "Failed to load details");
      }
    } catch (error) {
      console.error("Fetch international detail error:", error);
      setViewError(
        error?.response?.data?.message || "Failed to load details"
      );
    } finally {
      setViewLoading(false);
    }
  };

  const closeViewModal = () => {
    setShowModal(false);
    setViewCollege(null);
    setViewError("");
  };

  return (
    <div className="page-wrapper">
      <div className="container-fluid mt-3">
        <div className="card shadow border-0">
          {/* Header */}
          <div className="card-header bg-white">
            <div className="row align-items-center g-3">
              <div className="col-md-3">
                <h4 className="fw-bold mb-0">
                  <MdPublic className="me-2 text-primary" />
                  International Colleges
                </h4>
              </div>

              <div className="col-md-4">
                <div className="input-group">
                  <span className="input-group-text">
                    <MdSearch />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search university name..."
                    value={searchTerm}
                    onChange={handleSearch}
                  />
                </div>
              </div>

              <div className="col-md-5">
                <select
                  className="form-select"
                  value={countryFilter}
                  onChange={(e) => {
                    setCountryFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">All Countries ({pagination.total})</option>
                  {countries.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-bordered table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "60px" }}>#</th>
                    <th>University</th>
                    <th>Country</th>
                    <th>Type</th>
                    <th>Degree</th>
                    <th>Duration</th>
                    <th>Established</th>
                    <th>ECFMG</th>
                    <th>Grade</th>
                    <th>Indian Students</th>
                    <th style={{ width: "90px" }} className="text-center">
                      Featured
                    </th>
                    <th style={{ width: "110px" }}>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    [...Array(8)].map((_, i) => (
                      <tr key={i}>
                        {[...Array(12)].map((__, j) => (
                          <td key={j}>
                            <Skeleton />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : data.length > 0 ? (
                    data.map((item, index) => (
                      <tr key={item.id}>
                        <td>{(page - 1) * limit + index + 1}</td>

                        {/* University */}
                        <td>
                          <div className="d-flex align-items-center">
                            {item.img && (
                              <img
                                src={item.img}
                                alt=""
                                className="rounded-circle me-2"
                                width="40"
                                height="40"
                                style={{ objectFit: "cover" }}
                              />
                            )}
                            <div>
                              <strong>{item.university_name || "-"}</strong>
                              {item.slug && (
                                <div className="small text-muted">
                                  {item.slug}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Country */}
                        <td>
                          {item.country_name ? (
                            <span className="badge bg-primary">
                              {item.country_name}
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>

                        {/* Type */}
                        <td>{item.university_type || "-"}</td>

                        {/* Degree */}
                        <td>
                          {item.degree_awarded || item.degree_name || "-"}
                        </td>

                        {/* Duration */}
                        <td>{item.study_year || "-"}</td>

                        {/* Established */}
                        <td>{item.established_year || "-"}</td>

                        {/* ECFMG */}
                        <td className="text-center">
                          {item.is_ecfmg_approved ? (
                            <span className="badge bg-success">Yes</span>
                          ) : (
                            <span className="badge bg-secondary">No</span>
                          )}
                        </td>

                        {/* Grade */}
                        <td>
                          {item.grade ? (
                            <span className="badge bg-info text-light">
                              {item.grade}
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>

                        {/* Indian Students */}
                        <td>{item.indian_students || "-"}</td>

                        {/* Featured toggle */}
                        <td className="text-center">
                          <div className="form-check form-switch d-flex justify-content-center m-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              checked={!!item.is_featured}
                              disabled={featuredLoading[item.id]}
                              onChange={() =>
                                toggleFeatured(
                                  item.id,
                                  !!item.is_featured
                                )
                              }
                              style={{
                                cursor: "pointer",
                                width: "42px",
                                height: "22px",
                              }}
                              title={
                                item.is_featured
                                  ? "Remove from featured"
                                  : "Mark as featured on home page"
                              }
                            />
                          </div>
                        </td>

                        {/* Actions — NO EDIT for international */}
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() =>
                              openViewModal(item.slug || item.id)
                            }
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="12" className="text-center py-5">
                        <div className="text-muted">
                          <MdPublic size={40} />
                          <div className="mt-2">
                            No International Colleges Found
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="text-muted">
                  Showing{" "}
                  {Math.min((page - 1) * limit + 1, pagination.total)} -{" "}
                  {Math.min(page * limit, pagination.total)} of{" "}
                  {pagination.total} universities
                </div>

                <div className="d-flex align-items-center gap-2">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                  >
                    Previous
                  </button>

                  <span>
                    Page <strong>{page}</strong> of{" "}
                    <strong>{pagination.totalPages}</strong>
                  </span>

                  <button
                    className="btn btn-outline-secondary btn-sm"
                    disabled={page === pagination.totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* View Modal */}
      {showModal && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{ background: "rgba(0,0,0,0.5)" }}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeViewModal();
          }}
        >
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {viewCollege?.university_name || "University Details"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={closeViewModal}
                >
                  <MdClose style={{ display: "none" }} />
                </button>
              </div>

              <div className="modal-body">
                {viewLoading && (
                  <div className="text-center py-4">
                    <Skeleton count={6} />
                  </div>
                )}

                {!viewLoading && viewError && (
                  <div className="alert alert-danger mb-0">{viewError}</div>
                )}

                {!viewLoading && !viewError && viewCollege && (
                  <>
                    {/* Featured status pill */}
                    <div className="mb-3">
                      {viewCollege.is_featured ? (
                        <span className="badge bg-warning text-dark">
                          ⭐ Featured on Home Page
                        </span>
                      ) : (
                        <span className="badge bg-secondary">
                          Not Featured
                        </span>
                      )}
                    </div>

                    {/* Basic details */}
                    <div className="row g-3 mb-4">
                      <div className="col-md-6">
                        <strong>Slug:</strong> {viewCollege.slug || "-"}
                      </div>
                      <div className="col-md-6">
                        <strong>Country:</strong>{" "}
                        {viewCollege.country_name || "-"}
                      </div>
                      <div className="col-md-6">
                        <strong>University Type:</strong>{" "}
                        {viewCollege.university_type || "-"}
                      </div>
                      <div className="col-md-6">
                        <strong>Language:</strong>{" "}
                        {viewCollege.university_language || "-"}
                      </div>
                      <div className="col-md-6">
                        <strong>Degree:</strong>{" "}
                        {viewCollege.degree_awarded ||
                          viewCollege.degree_name ||
                          "-"}
                      </div>
                      <div className="col-md-6">
                        <strong>Duration:</strong>{" "}
                        {viewCollege.study_year || "-"}
                      </div>
                      <div className="col-md-6">
                        <strong>Established:</strong>{" "}
                        {viewCollege.established_year || "-"}
                      </div>
                      <div className="col-md-6">
                        <strong>Grade:</strong> {viewCollege.grade || "-"}
                      </div>
                      <div className="col-md-6">
                        <strong>ECFMG Approved:</strong>{" "}
                        {viewCollege.is_ecfmg_approved ? "Yes" : "No"}
                      </div>
                      <div className="col-md-6">
                        <strong>Indian Students:</strong>{" "}
                        {viewCollege.indian_students || "-"}
                      </div>
                      <div className="col-md-6">
                        <strong>Indian Mess:</strong>{" "}
                        {viewCollege.indian_mess || "-"}
                      </div>
                      <div className="col-md-6">
                        <strong>Program Type:</strong>{" "}
                        {viewCollege.program_type || "-"}
                      </div>
                      <div className="col-md-6">
                        <strong>Total Budget:</strong>{" "}
                        {viewCollege.total_budget
                          ? `${viewCollege.currency || ""} ${viewCollege.total_budget}`.trim()
                          : "-"}
                      </div>
                      <div className="col-md-6">
                        <strong>Service Amount:</strong>{" "}
                        {viewCollege.service_amount || "-"}
                      </div>
                      <div className="col-md-6">
                        <strong>Agent Commission:</strong>{" "}
                        {viewCollege.agent_commission_amount
                          ? `${viewCollege.agent_commission_currency || ""} ${viewCollege.agent_commission_amount}`.trim()
                          : "-"}
                      </div>
                    </div>

                    {/* Images */}
                    {(viewCollege.img || viewCollege.logo) && (
                      <div className="mb-4">
                        <h6 className="fw-bold">Images</h6>
                        <div className="d-flex gap-3">
                          {viewCollege.img && (
                            <img
                              src={viewCollege.img}
                              alt="University"
                              style={{
                                maxWidth: "200px",
                                borderRadius: "8px",
                              }}
                            />
                          )}
                          {viewCollege.logo && (
                            <img
                              src={viewCollege.logo}
                              alt="Logo"
                              style={{
                                maxWidth: "80px",
                                maxHeight: "80px",
                                borderRadius: "8px",
                              }}
                            />
                          )}
                        </div>
                      </div>
                    )}

                    {/* Description */}
                    {viewCollege.description && (
                      <div className="mb-2">
                        <h6 className="fw-bold">Description</h6>
                        <div
                          className="border rounded p-3 bg-light"
                          style={{ maxHeight: "400px", overflow: "auto" }}
                          dangerouslySetInnerHTML={{
                            __html: viewCollege.description,
                          }}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeViewModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InternationalCollegeList;